import { useEffect, useMemo, useState } from 'react';
import { Dropdown } from 'antd';

import { GenericPrefixedInputBoxControl } from 'components/molecules/PPA/commons';
import { colorConstants } from 'config/colorConstants';
import { ppaSimTableBulkUpdateInputTestId, ppaSimTableBulkUpdateOptionTestId } from 'constants/ppaTestIds';
import type { SimulationBulkUpdateConfig, SimulationBulkUpdateField } from 'types/ppaSimulationMeta';

type KpiBulkUpdateHeaderProps = {
    groupKey: string;
    title: string;
    bulkUpdate: SimulationBulkUpdateConfig;
    onApply: (field: SimulationBulkUpdateField, value: string) => void;
};

const isBulkUpdateDisabled = (bulkUpdate: SimulationBulkUpdateConfig, field: SimulationBulkUpdateField) =>
    bulkUpdate.isLocked ||
    bulkUpdate.editMode === 'none' ||
    field.isLocked ||
    field.editMode === 'none';

const KpiBulkUpdateHeader = ({ groupKey, title, bulkUpdate, onApply }: KpiBulkUpdateHeaderProps) => {
    const [selectedFieldId, setSelectedFieldId] = useState(bulkUpdate.fields[0]?.id ?? '');
    const [appliedValue, setAppliedValue] = useState('');

    const selectedField = useMemo(
        () => bulkUpdate.fields.find((field) => field.id === selectedFieldId) ?? bulkUpdate.fields[0],
        [bulkUpdate.fields, selectedFieldId],
    );

    useEffect(() => {
        setSelectedFieldId((currentId) =>
            bulkUpdate.fields.some((field) => field.id === currentId) ? currentId : bulkUpdate.fields[0]?.id ?? '',
        );
        setAppliedValue('');
    }, [bulkUpdate.fields]);

    if (!selectedField) {
        return <span className="truncate text-[12px] font-normal leading-[18px]">{title}</span>;
    }

    const disabled = isBulkUpdateDisabled(bulkUpdate, selectedField);
    const suffixLabel = selectedField.suffix || selectedField.placeholder || '';
    const showFieldMenu = bulkUpdate.fields.length > 1;

    const handleApply = (nextValue: string) => {
        if (disabled) return;
        setAppliedValue(nextValue);
        onApply(selectedField, nextValue);
    };

    const suffix = suffixLabel ? <span>{suffixLabel}</span> : undefined;

    return (
        <div className="flex w-full min-w-0 items-center justify-between gap-1">
            <span className="truncate text-[12px] font-normal leading-[18px]">{title}</span>
            <GenericPrefixedInputBoxControl
                showSave
                disabled={disabled}
                value={appliedValue}
                onChange={handleApply}
                onSave={handleApply}
                allowedValueType={selectedField.valueType === 'number' ? 'number' : 'any'}
                allowDecimal
                decimalPlaces={2}
                aria-label={selectedField.label || bulkUpdate.label}
                data-testid={ppaSimTableBulkUpdateInputTestId(groupKey)}
                suffix={
                    showFieldMenu ? (
                        <Dropdown
                            trigger={['click']}
                            disabled={disabled}
                            menu={{
                                selectedKeys: [selectedField.id],
                                items: bulkUpdate.fields.map((field) => ({
                                    key: field.id,
                                    disabled: isBulkUpdateDisabled(bulkUpdate, field),
                                    label: (
                                        <span data-testid={ppaSimTableBulkUpdateOptionTestId(groupKey, field.id)}>
                                            {field.label}
                                        </span>
                                    ),
                                })),
                                onClick: ({ key, domEvent }) => {
                                    domEvent.stopPropagation();
                                    setSelectedFieldId(key);
                                    setAppliedValue('');
                                },
                            }}
                        >
                            <button
                                type="button"
                                className="inline-flex items-center border-0 bg-transparent p-0"
                                aria-label={bulkUpdate.label}
                                onClick={(event) => event.stopPropagation()}
                            >
                                {suffix}
                            </button>
                        </Dropdown>
                    ) : (
                        suffix
                    )
                }
                className="kpi-bulk-update-input table-cell-input !h-full !rounded-[4px] !border-[0.5px] !px-[6px] [&_.ant-input-prefix]:!me-[2px] [&_.ant-input-prefix]:!text-[13px] [&_.ant-input]:!text-[13px] [&_.ant-input]:!leading-[18px] [&_.ant-input]:!text-right"
                containerClassName="!w-[72px] !min-w-[72px] overflow-visible"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
                style={{
                    borderColor: colorConstants.tableCellBorder,
                    color: colorConstants.tableMutedText,
                    background: 'transparent',
                }}
            />
        </div>
    );
};

export default KpiBulkUpdateHeader;
